import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { $$toast } from '@doger/ui/components/ui/toast';
import { $$api, ErrorMatcher } from '@doger/core/shared/api';

import { useAuth } from '@doger/chat/modules/auth/auth.context';

import { useTranslation } from '../../../../shared/i18n';

type UseChatQueryParams = {
  sessionId: string;
};

export const useChatQuery = ({ sessionId }: UseChatQueryParams) => {
  const navigate = useNavigate();
  const { setIsEmailConfirmationModalOpen } = useAuth();
  const { t } = useTranslation('app');

  return useQuery({
    queryKey: ['auth', 'session', sessionId],
    queryFn: async () => {
      try {
        const session = await $$api.getSession({ sessionId: sessionId });

        return session;
      } catch (error) {
        switch (true) {
          case ErrorMatcher.isAppError(error, 'NOT_ON_WHITELIST'): {
            navigate('/');
            throw error;
          }

          case ErrorMatcher.isAppError(error, 'EMAIL_NOT_CONFIRMED'): {
            $$toast.error(t('auth.emailNotConfirmed'), {
              action: {
                label: t('auth.emailConfirmationReminder.actionLabel', {
                  ns: 'core',
                }),
                onClick: () => {
                  setIsEmailConfirmationModalOpen(true);
                },
              },
            });
            throw error;
          }

          case ErrorMatcher.isAppError(error, 'CHAT_NOT_FOUND'): {
            navigate('/');
            $$toast.error(t('errors.chat.notFound'));
            throw error;
          }

          case ErrorMatcher.isAbortErrorOrCanceledError(error): {
            throw error;
          }

          default: {
            $$toast.error(t('errors.chat.failedToLoad'));
            throw error;
          }
        }
      }
    },
    refetchInterval: 60_000,
  });
};
